import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

// Simplified types for ControlDeck
export interface LogEntry {
  id: string
  timestamp: number
  level: 'info' | 'warn' | 'error' | 'debug'
  source: string
  message: string
}

export interface Agent {
  id: string
  name: string
  status: 'idle' | 'busy' | 'error'
  model: string
  currentTask?: string
}

export interface BuildMetrics {
  tokensUsed: number
  estimatedCost: number
  filesCreated: number
  filesModified: number
  completedTasks: number
  totalTasks: number
}

export interface Build {
  id: string
  status: 'idle' | 'running' | 'completed' | 'failed'
  startedAt?: number
  completedAt?: number
  wave?: number
  metrics?: BuildMetrics
}

interface BuildState {
  connected: boolean
  logs: LogEntry[]
  autoScroll: boolean
  logFilter: string
  build: Build | null
  agents: Map<string, Agent>

  setConnected: (connected: boolean) => void
  addLog: (entry: LogEntry) => void
  clearLogs: () => void
  setAutoScroll: (auto: boolean) => void
  setLogFilter: (filter: string) => void
  setBuild: (build: Build | null) => void
  updateAgent: (agent: Agent) => void
  removeAgent: (id: string) => void
}

const MAX_LOGS = 1000

export const useBuildStore = create<BuildState>()(
  subscribeWithSelector((set) => ({
    connected: false,
    logs: [],
    autoScroll: true,
    logFilter: '',
    build: null,
    agents: new Map(),

    setConnected: (connected) => set({ connected }),

    addLog: (entry) => set((state) => {
      const logs = [...state.logs, entry]
      if (logs.length > MAX_LOGS) {
        logs.splice(0, logs.length - MAX_LOGS)
      }
      return { logs }
    }),

    clearLogs: () => set({ logs: [] }),
    setAutoScroll: (auto) => set({ autoScroll: auto }),
    setLogFilter: (filter) => set({ logFilter: filter }),

    setBuild: (build) => set({ build }),

    updateAgent: (agent) => set((state) => {
      const agents = new Map(state.agents)
      agents.set(agent.id, agent)
      return { agents }
    }),

    removeAgent: (id) => set((state) => {
      const agents = new Map(state.agents)
      agents.delete(id)
      return { agents }
    })
  }))
)
